import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { callables, mensajeError } from '@/datos/acciones';
import { auth } from '@/lib/firebase';
import { esperarSesion, leerSesion, publicar } from '@/lib/sesion';
import { LogoTallerza } from '@/marca/Logo';
import { aplicarTemaTaller } from '@/lib/temaTaller';
import { Boton } from '@/ui/boton';
import { Campo, Entrada } from '@/ui/campo';
import { cn } from '@/ui/cn';

const busqueda = z.object({ redirect: z.string().optional().catch(undefined) });

export const Route = createFileRoute('/ingresar')({
  validateSearch: busqueda,
  beforeLoad: async () => {
    const s = await esperarSesion();
    if (s.estado === 'activa') throw redirect({ to: '/' });
  },
  component: Ingresar,
});

const esquema = z.object({
  email: z.email('Escribe un correo válido'),
  password: z.string().min(1, 'Escribe tu contraseña'),
});

function Ingresar() {
  // Signing in is always Tallerza: clear a workshop theme left by a session
  // that ended without unmounting its shell (revoked token, closed tab).
  useEffect(() => aplicarTemaTaller(null), []);
  const { redirect: destino } = Route.useSearch();
  const navegar = useNavigate();
  const [ver, setVer] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviandoEnlace, setEnviandoEnlace] = useState(false);
  const form = useForm<z.infer<typeof esquema>>({
    resolver: zodResolver(esquema),
    defaultValues: { email: '', password: '' },
  });

  const enviar = form.handleSubmit(async ({ email, password }) => {
    setError(null);
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
      const s = await leerSesion(cred.user, true);
      if (s.estado === 'sin_permisos' && s.equipo) {
        // A shared tablet account has no backoffice: do not keep it signed in.
        await signOut(auth);
        setError(
          'Esta es la cuenta de un equipo compartido: se usa solo en la app de inspección de la tablet. Ingresa con tu correo personal.',
        );
        return;
      }
      publicar(s);
      if (s.estado === 'activa' && s.debeCambiarPassword) {
        await navegar({ to: '/cambiar-clave', replace: true });
        return;
      }
      if (s.estado !== 'activa') {
        setError(
          'Tu cuenta existe pero todavía no tiene un taller ni un rol asignado. Pídele al administrador que te habilite.',
        );
        return;
      }
      // Only follow same-app relative redirects (no open redirect).
      const seguro =
        destino && destino.startsWith('/') && !destino.startsWith('//') ? destino : '/';
      await navegar({ href: seguro, replace: true });
    } catch (e) {
      setError(mensajeError(e));
    }
  });

  const recuperar = async () => {
    const email = form.getValues('email').trim();
    if (!z.email().safeParse(email).success) {
      form.setError('email', { message: 'Escribe tu correo para enviarte el enlace' });
      form.setFocus('email');
      return;
    }
    setEnviandoEnlace(true);
    try {
      // Our own branded e-mail (solicitarRestablecerClave): the answer is the
      // same whether the account exists or not, and so is the message.
      await callables.solicitarRestablecerClave({ email });
      toast.success('Si ese correo tiene una cuenta, te llegará un enlace', {
        description: `Revisa ${email} (también la carpeta de spam). El enlace vence en 1 hora.`,
      });
    } catch (e) {
      toast.error(mensajeError(e));
    } finally {
      setEnviandoEnlace(false);
    }
  };

  const { errors, isSubmitting } = form.formState;

  return (
    <main className="grid min-h-dvh bg-bg lg:grid-cols-2">
      <section className="hidden flex-col justify-between gap-10 bg-surface-muted px-16 py-14 lg:flex">
        <LogoTallerza tamano="lg" />
        <div className="flex max-w-[520px] flex-col gap-5">
          <p className="titulo text-[44px] leading-[1.06] tracking-[-0.045em] text-balance text-text xl:text-[52px] xl:leading-[1.04]">
            Cada auto de tu taller, con su fase y su evidencia.
          </p>
          <p className="text-[19px] leading-normal text-text-2">
            Tablero en tiempo real, inspecciones firmadas con fotos verificadas y avisos al cliente
            en un solo lugar.
          </p>
        </div>
        <PieKalfuntech />
      </section>

      <section className="flex flex-col px-6 py-10 sm:px-12 lg:justify-center lg:py-14">
        <LogoTallerza tamano="sm" className="lg:hidden" />
        <div className="mx-auto my-auto w-full max-w-[420px] py-10 lg:my-0 lg:py-0">
          <h1 className="titulo text-[32px] leading-tight tracking-[-0.035em] sm:text-[36px]">
            Hola de nuevo
          </h1>
          <p className="mt-2 text-[16px] leading-normal text-text-2">
            Usa el correo y la contraseña que te dio el administrador de tu taller.
          </p>

          <form onSubmit={enviar} className="mt-8 flex flex-col gap-5" noValidate>
            <Campo etiqueta="Correo" error={errors.email?.message}>
              {(p) => (
                <Entrada
                  {...p}
                  {...form.register('email')}
                  type="email"
                  autoComplete="username"
                  inputMode="email"
                  placeholder="nombre@taller.cl"
                  autoFocus
                  className="h-[52px] rounded-[14px] text-base"
                />
              )}
            </Campo>
            <Campo etiqueta="Contraseña" error={errors.password?.message}>
              {(p) => (
                <div className="relative">
                  <Entrada
                    {...p}
                    {...form.register('password')}
                    type={ver ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="h-[52px] rounded-[14px] pr-12 text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setVer((v) => !v)}
                    className="absolute top-1/2 right-1.5 grid size-9 -translate-y-1/2 place-items-center rounded-[8px] text-text-2 hover:bg-surface-muted hover:text-text"
                    aria-label={ver ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    aria-pressed={ver}
                  >
                    {ver ? <EyeOff className="size-[18px]" /> : <Eye className="size-[18px]" />}
                  </button>
                </div>
              )}
            </Campo>

            {error && (
              <p
                role="alert"
                className="animate-aparecer rounded-[12px] border border-que-strong/35 bg-que-soft px-4 py-3 text-[14px] leading-snug text-que-texto"
              >
                {error}
              </p>
            )}

            <Boton
              type="submit"
              variante="primario"
              tamano="lg"
              cargando={isSubmitting}
              className="mt-1"
            >
              <LogIn />
              Ingresar
            </Boton>
            <button
              type="button"
              onClick={recuperar}
              disabled={enviandoEnlace}
              aria-busy={enviandoEnlace || undefined}
              className="self-center text-[15px] text-primary-text underline-offset-4 hover:underline disabled:opacity-60 disabled:hover:no-underline"
            >
              {enviandoEnlace ? 'Enviando enlace…' : 'Olvidé mi contraseña'}
            </button>
          </form>
        </div>
        <PieKalfuntech className="text-center lg:hidden" />
      </section>
    </main>
  );
}

function PieKalfuntech({ className }: { className?: string }) {
  return (
    <p className={cn('text-[14px] text-text-2 lg:text-[15px]', className)}>
      Tallerza es un producto de Kalfuntech
    </p>
  );
}
